import { CircularProgress, Pagination } from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import SearchUtilities from '../components/pages/search/SearchUtilities';
import ProductCard from '../components/ProductCard';
import SearchSidePanel, {
  ProducerForSearch,
} from '../components/SearchSidePanel';
import { useCategory } from '../context/CategoryContext';
import { useParameter } from '../context/ParameterContext';
import { useProduct } from '../context/ProductContext';
import { useSearch } from '../context/SearchContext';
import { Category } from '../models/Category';
import { Parameter } from '../models/Parameter';
import { Product } from '../models/Product';
import { useProducer } from '../context/ProducerContext';
import { useConfig } from '../utils/useConfig';
import Seo from '../components/seo/Seo';

const Search = () => {
  const config = useConfig();

  // Stan dla parametrów URL i wyszukiwania
  const [searchParams, setSearchParams] = useSearchParams();
  const search = searchParams.get('search');
  const categoryId = searchParams.get('categoryId');
  const page = searchParams.get('page')
    ? parseInt(searchParams.get('page')!)
    : 1;
  const minPrice = searchParams.get('minPrice');
  const maxPrice = searchParams.get('maxPrice');
  const filters = searchParams.get('filters');

  // Parsowanie producentów w lepszy sposób
  const producersParam = searchParams.get('producers');
  const [selectedProducers, setSelectedProducers] = useState<number[]>(() => {
    if (!producersParam) return [];

    try {
      const parsedProducers = JSON.parse(decodeURIComponent(producersParam));
      return parsedProducers;
    } catch (error) {
      console.error('Error parsing producers from URL:', error);
      return [];
    }
  });

  // Parsowanie filtrów z URL
  const filtersParam = searchParams.get('filters');
  const [selectedFilters, setSelectedFilters] = useState<{
    [key: string]: string[];
  }>(() => {
    if (!filtersParam) return {};

    try {
      return JSON.parse(decodeURIComponent(filtersParam));
    } catch (error) {
      console.error('Error parsing filters from URL:', error);
      return {};
    }
  });

  // Stan UI
  const [displayMode, setDisplayMode] = useState<'grid' | 'list'>('grid');
  const [limit, setLimit] = useState<number>(10);
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const [orderBy, setOrderBy] = useState<'date' | 'price'>('date');
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Stan danych produktów
  const [productList, setProductList] = useState<Product[]>([]);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [totalProducts, setTotalProducts] = useState<number>(0);
  const [priceRange, setPriceRange] = useState<number[]>([0, 100000]);
  const [currentPrice, setCurrentPrice] = useState<number[]>([
    minPrice ? parseInt(minPrice) : 0,
    maxPrice ? parseInt(maxPrice) : 100000,
  ]);

  // Stan danych kategorii i parametrów
  const [categoryAncestry, setCategoryAncestry] = useState<Category[]>([]);
  const [currentCategory, setCurrentCategory] = useState<Category | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [parameters, setParameters] = useState<Parameter[]>([]);

  // Stan danych producentów
  const [producers, setProducers] = useState<ProducerForSearch[]>([]);
  const { getProducersForCategory } = useProducer();

  // Importuj konteksty
  const { getProducts } = useProduct();
  const { getCategories } = useCategory();
  const { getParametersForCategoryWithValues } = useParameter();
  const { isSidePanelOpen, setIsSidePanelOpen } = useSearch();

  // Refs i style
  const sidePanelRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const isDesktop = window.innerWidth > 768;

  useEffect(() => {
    if (!isDesktop) {
      setDisplayMode('list');
    }
  }, [isDesktop]);

  // Funkcja pobierająca produkty - usuwamy filters z zależności
  const fetchProducts = useCallback(async () => {
    setIsLoading(true);
    try {
      const { products, totalPages, totalProducts, maxPrice, minPrice } =
        await getProducts({
          draft: false,
          page: page,
          limit: limit,
          query: search || '',
          categoryId: categoryId ? parseInt(categoryId) : null,
          // minPrice: currentPrice[0],
          // maxPrice: currentPrice[1],
          // Tymczasowo przekazujemy puste filtry
          filters: selectedFilters,
          order: order,
          orderBy: orderBy,
          producerIds:
            selectedProducers.length > 0 ? selectedProducers : undefined, // Dodajemy producerIds do zapytania
        });

      setProductList(products);
      setTotalPages(totalPages);
      setTotalProducts(totalProducts);

      // Aktualizuj zakres cen tylko jeśli faktycznie się zmieniły
      if (
        !priceRange ||
        priceRange[0] !== minPrice ||
        priceRange[1] !== maxPrice
      ) {
        setPriceRange([minPrice, maxPrice]);

        // Ustaw aktualny zakres cen jeśli nie został ustawiony w URL
        if (!minPrice || !maxPrice) {
          setCurrentPrice([minPrice, maxPrice]);
        }
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Wystąpił błąd podczas ładowania produktów');
    } finally {
      setIsLoading(false);
    }
  }, [
    categoryId,
    currentPrice,
    filters,
    getProducts,
    limit,
    page,
    search,
    order,
    orderBy,
    // priceRange,
    selectedProducers, // Dodajemy selectedProducers do zależności
  ]);

  // Funkcja pobierająca kategorie
  const fetchCategories = useCallback(async () => {
    try {
      const { categories, ancestors, currentCategory } = await getCategories(
        parseInt(categoryId!),
      );
      setCategories(categories);
      setCategoryAncestry(ancestors);
      setCurrentCategory(currentCategory);
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast.error('Wystąpił błąd podczas ładowania kategorii');
    }
  }, [categoryId, getCategories]);

  // Funkcja pobierająca parametry
  const fetchParameters = useCallback(async () => {
    if (!categoryId) {
      setParameters([]);
      return;
    }

    try {
      const response = await getParametersForCategoryWithValues(
        parseInt(categoryId),
      );
      setParameters(response);
    } catch (error) {
      console.error('Error fetching parameters:', error);
      toast.error('Wystąpił błąd podczas ładowania parametrów');
    }
  }, [categoryId, getParametersForCategoryWithValues]);

  // Funkcja pobierająca producentów dla danej kategorii
  const fetchProducers = useCallback(async () => {
    try {
      // Jeśli nie ma wybranej kategorii, pobierz wszystkich producentów (null oznacza wszystkich)
      const categoryIdForProducers = categoryId ? parseInt(categoryId) : null;
      const producersList = await getProducersForCategory(
        categoryIdForProducers!,
      );

      // Dodajmy liczbę produktów dla każdego producenta, jeśli ta informacja jest dostępna
      const enrichedProducers = producersList.map((producer) => ({
        ...producer,
        // Opcjonalnie możemy dodać productCount, jeśli API zwraca takie dane
        productCount: producer.productCount || 0,
      }));

      setProducers(enrichedProducers);
    } catch (error) {
      console.error('Error fetching producers:', error);
      toast.error('Wystąpił błąd podczas ładowania producentów');
    }
  }, [categoryId, getProducersForCategory]);

  // Poprawiona funkcja handleFilterChange
  const handleFilterChange = useCallback(
    (parameterId: string, value: string, isSelected: boolean) => {
      // Utwórz nowy obiekt filtrów
      const newSelectedFilters = { ...selectedFilters };
      const prevValues = newSelectedFilters[parameterId] || [];

      if (isSelected) {
        // Dodaj wartość do filtrów
        newSelectedFilters[parameterId] = [...prevValues, value];
      } else {
        // Usuń wartość z filtrów
        const newValues = prevValues.filter((v) => v !== value);
        if (newValues.length === 0) {
          // Jeśli nie ma więcej wartości, usuń cały klucz parametru
          delete newSelectedFilters[parameterId];
        } else {
          newSelectedFilters[parameterId] = newValues;
        }
      }

      // Aktualizuj stan
      setSelectedFilters(newSelectedFilters);

      // Aktualizacja URL
      const newSearchParams = new URLSearchParams(searchParams);
      if (Object.keys(newSelectedFilters).length > 0) {
        const filtersString = encodeURIComponent(
          JSON.stringify(newSelectedFilters),
        );
        newSearchParams.set('filters', filtersString);
      } else {
        newSearchParams.delete('filters');
      }

      // Resetuj stronę przy zmianie filtrów
      newSearchParams.set('page', '1');
      setSearchParams(newSearchParams);
    },
    [searchParams, setSearchParams, selectedFilters],
  );

  // Poprawiona funkcja handleClearAllFilters
  const handleClearAllFilters = useCallback(() => {
    setSelectedFilters({});

    // Aktualizacja URL - usunięcie parametru filters
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete('filters');

    // Resetujemy stronę przy zmianie filtrów
    newSearchParams.set('page', '1');
    setSearchParams(newSearchParams);
  }, [searchParams, setSearchParams]);

  // Zastosowanie filtrów do URL
  const handleApplyFilters = useCallback(
    (newFilters: { [key: string]: string[] }) => {
      // Aktualizujemy stan filtrów
      setSelectedFilters(newFilters);

      // Aktualizujemy URL
      const newSearchParams = new URLSearchParams(searchParams);

      if (Object.keys(newFilters).length > 0) {
        const filtersString = encodeURIComponent(JSON.stringify(newFilters));
        newSearchParams.set('filters', filtersString);
      } else {
        newSearchParams.delete('filters');
      }

      // Resetujemy stronę przy zmianie filtrów
      newSearchParams.set('page', '1');
      setSearchParams(newSearchParams);
    },
    [searchParams, setSearchParams],
  );

  // Aktualizacja zakresu cen
  const handlePriceRangeChange = useCallback((range: number[]) => {
    setCurrentPrice(range);
  }, []);

  // Efekt pobierający dane - usunięcie filters jako wyzwalacza odświeżenia
  useEffect(() => {
    const fetchData = async () => {
      await fetchProducts();
    };

    fetchData();
  }, [
    categoryId,
    limit,
    // filters, // Usuwamy filters z zależności
    page,
    search,
    order,
    orderBy,
    fetchProducts,
  ]);

  useEffect(() => {
    fetchCategories();
    fetchParameters();
    fetchProducers(); // Dodajemy wywołanie funkcji pobierającej producentów
  }, [categoryId, fetchCategories, fetchParameters, fetchProducers]);

  // Obsługa kliknięć poza panelem bocznym
  useEffect(() => {
    if (isDesktop) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        sidePanelRef.current &&
        !sidePanelRef.current.contains(event.target as Node)
      ) {
        setIsSidePanelOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [setIsSidePanelOpen, isDesktop]);

  // Aktualizacja tytułu strony

  // Obsługa responsywności dla siatki produktów
  useEffect(() => {
    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      if (!entries || entries.length === 0) return;
      const { width } = entries[0].contentRect;
      setContainerWidth(width);
    });

    resizeObserver.observe(containerRef.current);
    return () => resizeObserver.disconnect();
  }, []);

  // Obliczanie liczby kolumn
  const cardWidth = 256;
  const gap = 16;
  const columns = containerWidth
    ? Math.max(1, Math.floor((containerWidth + gap) / (cardWidth + gap)))
    : 1;

  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
    gap: `${gap}px`,
  };

  // Obsługa paginacji
  const handlePageChange = (_: React.ChangeEvent<unknown>, newPage: number) => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('page', newPage.toString());
    setSearchParams(newSearchParams);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Obsługa zmiany producenta
  const handleProducerChange = useCallback(
    (producerId: number, isSelected: boolean) => {
      const newSelectedProducers = isSelected
        ? [...selectedProducers, producerId]
        : selectedProducers.filter((id) => id !== producerId);

      setSelectedProducers(newSelectedProducers);

      // Aktualizacja URL - formatujemy jako JSON dla spójności
      const newSearchParams = new URLSearchParams(searchParams);
      if (newSelectedProducers.length > 0) {
        newSearchParams.set(
          'producers',
          encodeURIComponent(JSON.stringify(newSelectedProducers)),
        );
      } else {
        newSearchParams.delete('producers');
      }

      // Resetujemy stronę przy zmianie filtrów
      newSearchParams.set('page', '1');
      setSearchParams(newSearchParams);
    },
    [searchParams, setSearchParams, selectedProducers],
  );

  // Dodaj nową funkcję handleClearAllProducers
  const handleClearAllProducers = useCallback(() => {
    // Resetujemy stan wybranych producentów
    setSelectedProducers([]);

    // Aktualizujemy URL - usuwamy parametr producers
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete('producers');

    // Resetujemy stronę przy zmianie filtrów
    newSearchParams.set('page', '1');
    setSearchParams(newSearchParams);
  }, [searchParams, setSearchParams]);

  // Synchronizacja selectedProducers z URL przy zmianie URL
  useEffect(() => {
    const producersFromUrl = searchParams.get('producers');

    if (!producersFromUrl) {
      // Jeśli nie ma producentów w URL, resetujemy stan
      if (selectedProducers.length > 0) {
        setSelectedProducers([]);
      }
      return;
    }

    try {
      const parsedProducers = JSON.parse(decodeURIComponent(producersFromUrl));
      // Porównujemy czy faktycznie trzeba aktualizować stan
      if (
        JSON.stringify(parsedProducers) !== JSON.stringify(selectedProducers)
      ) {
        setSelectedProducers(parsedProducers);
      }
    } catch (error) {
      console.error('Error parsing producers from URL:', error);
      setSelectedProducers([]);
    }
  }, [searchParams, selectedProducers]);

  // Synchronizacja filtrów z URL przy zmianie URL
  useEffect(() => {
    const filtersFromUrl = searchParams.get('filters');

    if (!filtersFromUrl) {
      setSelectedFilters({});
      return;
    }

    try {
      const parsedFilters = JSON.parse(decodeURIComponent(filtersFromUrl));
      setSelectedFilters(parsedFilters);
    } catch (error) {
      console.error('Error parsing filters from URL:', error);
      setSelectedFilters({});
    }
  }, [searchParams]);

  return (
    <>
      <div className="mx-auto px-4 my-6">
        <div className="flex flex-col md:grid md:grid-cols-12 gap-6">
          {/* Panel boczny - responsywny */}
          {isDesktop ? (
            <div className="col-span-3 h-fit bg-white rounded-lg shadow-sm">
              <SearchSidePanel
                currentCategory={currentCategory}
                categories={categories}
                parameters={parameters}
                searchParams={searchParams}
                setSearchParams={setSearchParams}
                filters={filters}
                priceRange={priceRange}
                currentPrice={currentPrice}
                setCurrentPrice={setCurrentPrice}
                setIsSidePanelOpen={setIsSidePanelOpen}
                producers={producers}
                selectedProducers={selectedProducers} // Przekazujemy selectedProducers
                onProducerChange={handleProducerChange} // Przekazujemy handler
                onClearAllProducers={handleClearAllProducers} // Nowy prop
                isSidePanelOpen={isSidePanelOpen}
                selectedFilters={selectedFilters}
                onFilterChange={handleFilterChange}
                onClearAllFilters={handleClearAllFilters}
                onApplyFilters={handleApplyFilters}
                onPriceRangeChange={handlePriceRangeChange}
                minPrice={currentPrice[0]}
                maxPrice={currentPrice[1]}
                totalPriceRange={priceRange}
              />
            </div>
          ) : (
            <SearchSidePanel
              currentCategory={currentCategory}
              categories={categories}
              parameters={parameters}
              searchParams={searchParams}
              setSearchParams={setSearchParams}
              filters={filters}
              priceRange={priceRange}
              currentPrice={currentPrice}
              setCurrentPrice={setCurrentPrice}
              setIsSidePanelOpen={setIsSidePanelOpen}
              producers={producers}
              selectedProducers={selectedProducers} // Przekazujemy selectedProducers
              onProducerChange={handleProducerChange} // Przekazujemy handler
              onClearAllProducers={handleClearAllProducers} // Nowy prop
              isSidePanelOpen={isSidePanelOpen}
              selectedFilters={selectedFilters}
              onFilterChange={handleFilterChange}
              onClearAllFilters={handleClearAllFilters}
              onApplyFilters={handleApplyFilters}
              onPriceRangeChange={handlePriceRangeChange}
              minPrice={currentPrice[0]}
              maxPrice={currentPrice[1]}
              totalPriceRange={priceRange}
            />
          )}

          {/* Główny obszar wyników */}
          <div className="col-span-9 space-y-6">
            {/* Ścieżka nawigacji */}
            {categoryAncestry && categoryAncestry.length > 0 && (
              <div className="text-sm breadcrumbs">
                <nav
                  aria-label="breadcrumb"
                  className="flex items-center space-x-1 text-gray-600"
                >
                  <a href="/" className="hover:text-blue-600">
                    Strona główna
                  </a>
                  {categoryAncestry.map((cat, index) => (
                    <React.Fragment key={cat.id}>
                      <span>/</span>
                      <a
                        href={`/szukaj?categoryId=${cat.id}`}
                        className={`${index === categoryAncestry.length - 1 ? 'font-medium text-gray-800' : 'hover:text-blue-600'}`}
                      >
                        {cat.name}
                      </a>
                    </React.Fragment>
                  ))}
                </nav>
              </div>
            )}

            {/* Panel narzędzi wyszukiwania */}
            <SearchUtilities
              setIsSidePanelOpen={setIsSidePanelOpen}
              isSidePanelOpen={isSidePanelOpen}
              displayMode={displayMode}
              setDisplayMode={setDisplayMode}
              limit={limit}
              setLimit={setLimit}
              orderBy={orderBy}
              setOrderBy={setOrderBy}
              order={order}
              setOrder={setOrder}
            />

            {/* Podsumowanie wyników */}
            <div className="flex justify-between items-center">
              <p className="text-gray-700">
                {isLoading ? (
                  <span className="flex items-center">
                    <CircularProgress size={16} className="mr-2" /> Ładowanie
                    produktów...
                  </span>
                ) : (
                  <>
                    Znaleziono{' '}
                    <span className="font-semibold">{totalProducts}</span>{' '}
                    produktów
                    {search && (
                      <>
                        {' '}
                        dla zapytania "
                        <span className="font-semibold">{search}</span>"
                      </>
                    )}
                  </>
                )}
              </p>
              {totalPages > 1 && (
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={handlePageChange}
                  color="primary"
                  size={isDesktop ? 'medium' : 'small'}
                />
              )}
            </div>

            {/* Lista produktów */}
            <div ref={containerRef} className="min-h-[300px]">
              {isLoading ? (
                <div className="flex justify-center items-center py-12">
                  <CircularProgress />
                </div>
              ) : (
                <>
                  {productList.length > 0 ? (
                    <div
                      className={displayMode === 'list' ? 'space-y-4' : ''}
                      style={displayMode === 'grid' ? gridStyle : {}}
                    >
                      {productList.map((product) => (
                        <ProductCard
                          key={product.id}
                          product={product}
                          vertical={displayMode === 'grid'}
                          className={`${displayMode === 'grid' ? '' : 'border-b pb-4 last:border-b-0'}`}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="bg-gray-50 rounded-lg p-8 text-center my-8">
                      <h3 className="text-xl font-medium text-gray-700 mb-2">
                        Brak produktów spełniających kryteria wyszukiwania
                      </h3>
                      <p className="text-gray-500">
                        Spróbuj zmienić filtry lub kryteria wyszukiwania
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Dolna paginacja */}
            {totalPages > 1 && (
              <div className="flex justify-center mt-8">
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={handlePageChange}
                  color="primary"
                  size={isDesktop ? 'medium' : 'small'}
                  showFirstButton
                  showLastButton
                />
              </div>
            )}
          </div>
        </div>
      </div>
      <Seo
        title={`${search ? `Wyniki dla: ${search}` : currentCategory ? `Kategoria: ${currentCategory.name}` : 'Wyszukiwanie'}`}
        description='Wyszukiwanie produktów'
      ></Seo>
    </>
  );
};

export default Search;
